import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import isEmpty from 'lodash/isEmpty';
import shuffle from 'lodash/shuffle';

import { BodyLoading } from '@cvt/components/layout/BodyLoading';

import { useFunFactsQuery } from '@modules/Assertions/hooks/useFunFactsQuery';
import { AuthContext } from '@modules/Auth/contexts/AuthContext/AuthContext';

import { ContextProps } from './types';


export const defaultContext: ContextProps = {
  getNewFunFact: (key?: string) => undefined,
};

export const FunFactContext = createContext(defaultContext);

export const FunFactProvider: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { funFacts, isLoading } = useFunFactsQuery({ limit: 100 }, { enabled: !!user });

  const remainingFunFactsRef = useRef<FunFacts.FunFact[]>([]);
  const previousFunFactRef = useRef<CVT.Maybe<FunFacts.FunFact>>(undefined);
  const currentKeyRef = useRef<CVT.Maybe<string>>(undefined);

  useEffect(() => {
    remainingFunFactsRef.current = [...funFacts];
  }, [funFacts]);

  const getNewFunFact = useCallback((key?: string) => {
    if (key === currentKeyRef.current && previousFunFactRef.current) {
      return previousFunFactRef.current;
    }

    let list = remainingFunFactsRef.current;

    if (isEmpty(list)) {
      list = [...funFacts];
    }

    list = shuffle(list);
    const funFact = list.pop();

    remainingFunFactsRef.current = list;

    previousFunFactRef.current = funFact;
    currentKeyRef.current = key;

    return funFact;
  }, [funFacts]);

  if (isLoading) {
    return <BodyLoading height="100%"/>;
  }

  return (
    <FunFactContext.Provider value={{ getNewFunFact }}>
      {children}
    </FunFactContext.Provider>
  );
};

export const UserConsumer = FunFactContext.Consumer;
